import { fetchConfig } from '@theme/utilities'
import { CartAddEvent, CartErrorEvent } from '@theme/events'

class PTabs extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null

  /** @type {HTMLImageElement | null} */
  #mainImg = null

  /** @type {NodeListOf<HTMLElement>} */
  #tabs = /** @type {any} */ (null)

  /** @type {NodeListOf<HTMLElement>} */
  #panels = /** @type {any} */ (null)

  /** @type {NodeListOf<HTMLElement>} */
  #thumbs = /** @type {any} */ (null)

  /** @type {NodeListOf<HTMLInputElement>} */
  #radios = /** @type {any} */ (null)

  /** @type {string[]} */
  #knownTitles = []

  /** @type {number} */
  #activeIndex = 0

  connectedCallback() {
    this.#controller = new AbortController()
    const { signal } = this.#controller

    this.#mainImg      = this.querySelector('.ptabs__main-img')
    this.#panels       = this.querySelectorAll('.ptabs__form[data-panel]')
    this.#thumbs       = this.querySelectorAll('.ptabs__thumb')
    this.#radios       = this.querySelectorAll('.ptabs__radio')
    this.#knownTitles  = [...this.#panels].map(p => p.dataset.variantTitle ?? '')
    this.#filterThumbs(this.#panels[0]?.dataset.variantTitle ?? '')

    this.#buildSwitcher()
    this.#tabs = this.querySelectorAll('[data-tab]')
    this.#activateTab(0)

    this.#tabs.forEach(btn =>
      btn.addEventListener('click', this.#handleTabClick, { signal })
    )

    this.#thumbs.forEach(btn =>
      btn.addEventListener('click', this.#handleThumbClick, { signal })
    )

    this.#radios.forEach(radio =>
      radio.addEventListener('change', this.#handleRadioChange, { signal })
    )

    this.querySelectorAll('.ptabs__buy-group').forEach(form =>
      form.addEventListener('submit', this.#handleFormSubmit, { signal })
    )

    this.querySelectorAll('.ptabs__buy-group--once').forEach(group =>
      group.addEventListener('click', this.#handleOnceGroupClick, { signal })
    )
  }

  disconnectedCallback() {
    this.#controller?.abort()
  }

  /** @param {MouseEvent} e */
  #handleTabClick = (e) => {
    const btn = /** @type {HTMLElement} */ (e.currentTarget)
    this.#activateTab(Number(btn.dataset.tab))
  }

  /** @param {MouseEvent} e */
  #handleOnceGroupClick = (e) => {
    const group = /** @type {HTMLElement} */ (e.currentTarget)
    const actions = group.querySelector('.ptabs__once-actions')
    if (!actions || actions.contains(/** @type {Node} */ (e.target))) return
    actions.toggleAttribute('hidden')
  }

  /** @param {Event} e */
  #handleRadioChange = (e) => {
    const radio = /** @type {HTMLInputElement} */ (e.currentTarget)
    const form = radio.closest('.ptabs__buy-group')
    if (!form) return
    const qtyInput = /** @type {HTMLInputElement | null} */ (form.querySelector('.ptabs__qty-input'))
    if (qtyInput) qtyInput.value = radio.dataset.qty ?? '1'
  }

  /** @param {SubmitEvent} e */
  #handleFormSubmit = async (e) => {
    e.preventDefault()
    const form = /** @type {HTMLFormElement} */ (e.currentTarget)
    const btn = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'))

    if (btn) btn.disabled = true

    try {
      const body = new FormData(form)

      const sectionIds = /** @type {string[]} */ (
        /** @type {HTMLElement[]} */ ([...document.querySelectorAll('cart-items-component[data-section-id]')])
          .map(el => el.dataset.sectionId)
          .filter(/** @param {string | undefined} id @returns {id is string} */ id => Boolean(id))
      )
      if (sectionIds.length) body.append('sections', sectionIds.join(','))

      const fetchCfg = fetchConfig('javascript', { body })
      const res = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      })

      const response = await res.json()

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent(form.id, response.message, response.description, response.errors))
        return
      }

      const updatedResponse = await this.#applyDiscount('SURPRISE', sectionIds)
    
      const finalSections = updatedResponse?.sections || response.sections

      this.dispatchEvent(new CartAddEvent(response, this.id, { sections: finalSections }))
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'))
      cartDrawer?.open()
    } catch (err) {
      console.error('[ptabs] cart/add error', err)
    } finally {
      if (btn) btn.disabled = false
    }
  }

  /**
   * @param {string} code
   * @param {string[]} sectionIds
   */
  #applyDiscount = async (code, sectionIds = []) => {
    try {
      const cart = await fetch('/cart.js').then(r => r.json())
      const alreadyApplied = (cart.cart_level_discount_applications || [])
        .some(d => d.title === code)
      if (alreadyApplied) return null

      const body = new FormData()
      body.append('discount', code)
      if (sectionIds.length) body.append('sections', sectionIds.join(','))

      const fetchCfg = fetchConfig('javascript', { body })
      const res = await fetch(Theme.routes.cart_update_url || '/cart/update.js', {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'application/json' },
      })

      if (!res.ok) {
        console.warn('[ptabs] discount apply failed', res.status)
        return null
      }

      const data = await res.json()
      return data  // ← возвращаем ответ с обновлёнными секциями
    } catch (err) {
      console.error('[ptabs] discount apply error', err)
      return null
    }
  }

  /** @param {MouseEvent} e */
  #handleThumbClick = (e) => {
    const btn = /** @type {HTMLElement} */ (e.currentTarget)
    this.#setActiveThumb(btn)
    this.#setMainImage(btn.dataset.fullUrl)
  }

  #buildSwitcher() {
    const switcher = this.querySelector('.ptabs__switcher')
    if (!switcher || this.#panels.length < 2) return

    this.#panels.forEach((panel, i) => {
      const label = panel.dataset.tabLabel
      if (!label) return
      const btn = document.createElement('button')
      btn.className = 'ptabs__tab' + (i === 0 ? ' is-active' : '')
      btn.type = 'button'
      btn.setAttribute('role', 'tab')
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false')
      btn.dataset.tab = String(i)
      btn.textContent = label
      switcher.append(btn)
    })

    switcher.removeAttribute('hidden')
  }

  /** @param {number} idx */
  #activateTab(idx) {
    this.#tabs.forEach((btn, i) => {
      btn.classList.toggle('is-active', i === idx)
      btn.setAttribute('aria-selected', i === idx ? 'true' : 'false')
    })

    this.#panels.forEach((panel, i) => {
      panel.classList.toggle('is-active', i === idx)
    })

    const panel = this.#panels[idx]
    this.#filterThumbs(panel?.dataset.variantTitle ?? '')
  }

  /** @param {string | undefined} url */
  #setMainImage(url) {
    if (!this.#mainImg || !url) return
    const makeW = (/** @type {number} */ w) => url.replace(/_([\d]+)x([\d]*)/, `_${w}x`)
    this.#mainImg.src = url
    this.#mainImg.srcset = [628, 800, 1000, 1256].map(w => `${makeW(w)} ${w}w`).join(', ')
    this.#mainImg.sizes = '(min-width: 990px) 50vw, 100vw'
  }

  /** @param {string} variantTitle */
  #filterThumbs(variantTitle) {
    let i = 0
    this.#thumbs.forEach(btn => {
      const alt = btn.dataset.mediaAlt ?? ''
      btn.hidden = this.#knownTitles.includes(alt) && alt !== variantTitle
      if (!btn.hidden) {
        btn.dataset.imageIndex = String(i++)
      } else {
        delete btn.dataset.imageIndex
      }
    })

    const target = [...this.#thumbs].find(btn => btn.dataset.imageIndex === String(this.#activeIndex))
                ?? [...this.#thumbs].find(btn => btn.dataset.imageIndex === '0')
    if (target) {
      this.#setActiveThumb(target)
      this.#setMainImage(target.dataset.fullUrl)
    }
  }

  /** @param {HTMLElement} activeBtn */
  #setActiveThumb(activeBtn) {
    this.#thumbs.forEach(btn => btn.classList.remove('is-active'))
    activeBtn.classList.add('is-active')
    this.#activeIndex = Number(activeBtn.dataset.imageIndex ?? 0)
  }
}

customElements.define('product-tabs', PTabs)
